import { useCallback, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'

import { type QuickTourTarget } from './build-tour-steps'
import { useTourSteps } from './use-tour-steps'

interface UseQuickTourOverlayStateProps {
  isQuickTourOpen: boolean
  quickTourStep: number
}

interface UseQuickTourOverlayStateResult {
  isQuickTourLocked: boolean
  getQuickTourSectionClass: (section: QuickTourTarget | 'history') => string
  /** Softening for panel chrome the tour never spotlights (e.g. the Day 0 banner). */
  getQuickTourDimClass: () => string
}

// The spotlight is the *lit* band, not a blackout: the section being taught
// turns opaque so it stands proud of the panel's translucent surface, while
// everything else just softens. Draining opacity (or adding brightness/saturate
// filters) washes light-on-light content out to nothing — the designs keep every
// section readable throughout.
//
// Only content *inside* the scroll flow softens. The footer is opaque chrome
// covering the scrolled content beneath it, so fading it would let that content
// show straight through the chat input.
const LIT = 'bg-background opacity-100'
const SOFTENED = 'opacity-40'
const TRANSITION = 'transition-[opacity,background-color] duration-200 motion-reduce:transition-none'

export function useQuickTourOverlayState({
  isQuickTourOpen,
  quickTourStep,
}: UseQuickTourOverlayStateProps): UseQuickTourOverlayStateResult {
  // Same derivation the tour card uses, so a step index can never mean one
  // section here and a different one there.
  const steps = useTourSteps()

  const activeQuickTourSection: QuickTourTarget | null = useMemo(() => {
    if (!isQuickTourOpen) return null
    const normalized = Math.min(Math.max(quickTourStep, 0), steps.length - 1)
    return steps[normalized].target
  }, [isQuickTourOpen, quickTourStep, steps])

  const isQuickTourLocked = isQuickTourOpen

  const getQuickTourSectionClass = useCallback(
    (section: QuickTourTarget | 'history') => {
      if (activeQuickTourSection == null) return ''
      return twMerge(TRANSITION, activeQuickTourSection === section ? LIT : SOFTENED)
    },
    [activeQuickTourSection],
  )

  const getQuickTourDimClass = useCallback(() => {
    if (activeQuickTourSection == null) return ''
    return twMerge(TRANSITION, SOFTENED)
  }, [activeQuickTourSection])

  return {
    isQuickTourLocked,
    getQuickTourSectionClass,
    getQuickTourDimClass,
  }
}
